// Wire contract — the solution-config OBSERVATION seam (ADR-056 §7). Producers
// (the data team's discovery job, field reports) post their LATEST sighting of a
// config field per site through ONE idempotent batch endpoint; drift is computed
// at read time, never stored. Shared by the backend endpoint (validate + parse +
// canonicalize) and the frontend (field kinds/units for labels) so the two sides
// can never disagree on the field vocabulary.
import { z } from 'zod';
import { BATTERY_CHEMISTRIES } from './requests.js';
import { TECHNOLOGY_CHEMISTRY } from '../common/battery.js';
// ── the observable field vocabulary ──────────────────────────────────────────
// One key per contestable config column of site_solution_configs — presence
// flags AND value fields (an undeclared / phantom source is a drift on the flag
// itself). Keys ARE the DB column names (varchar(48) in the observation store).
export const DISCOVERABLE_CONFIG_FIELDS = [
    'has_grid',
    'grid_provider',
    'grid_capacity_kw',
    'has_solar',
    'solar_capacity_kwp',
    'has_generator',
    'generator_capacity_kva',
    'generator_units',
    'generator_tank_l',
    'has_provider_generator',
    'provider_generator_name',
    'provider_generator_auto_failover',
    'has_battery',
    'battery_chemistry',
    'battery_capacity_ah',
    'has_wind',
    'wind_capacity_kw',
    'has_cooling',
    'cooling_units',
    'cooling_capacity_kw',
    'power_system_capacity_kw',
];
export const DiscoverableConfigFieldSchema = z.enum(DISCOVERABLE_CONFIG_FIELDS);
export const OBSERVABLE_FIELD_KIND = {
    has_grid: 'boolean',
    grid_provider: 'text',
    grid_capacity_kw: 'decimal',
    has_solar: 'boolean',
    solar_capacity_kwp: 'decimal',
    has_generator: 'boolean',
    generator_capacity_kva: 'decimal',
    generator_units: 'integer',
    generator_tank_l: 'integer',
    has_provider_generator: 'boolean',
    provider_generator_name: 'text',
    provider_generator_auto_failover: 'boolean',
    has_battery: 'boolean',
    battery_chemistry: 'battery_chemistry',
    battery_capacity_ah: 'integer',
    has_wind: 'boolean',
    wind_capacity_kw: 'decimal',
    has_cooling: 'boolean',
    cooling_units: 'integer',
    cooling_capacity_kw: 'decimal',
    power_system_capacity_kw: 'decimal',
};
/** Parse + canonicalize an observed value for a field. Returns the canonical
 *  text to store, or null for "observed absent"; throws on an unparseable value
 *  (the endpoint rejects that ROW, never the batch). Canonical forms: booleans
 *  'true'/'false'; numbers via String(Number(x)) so '80.0' ≡ '80'; text trimmed;
 *  battery technology one of the closed set. */
export function canonicalizeObservedValue(field, value) {
    if (value === null)
        return null;
    const v = value.trim();
    if (v === '')
        return null;
    switch (OBSERVABLE_FIELD_KIND[field]) {
        case 'boolean': {
            const low = v.toLowerCase();
            if (low === 'true' || low === 'false')
                return low;
            throw new Error(`expected 'true' or 'false' for ${field}`);
        }
        case 'integer': {
            if (!/^\d+$/.test(v))
                throw new Error(`expected a non-negative integer for ${field}`);
            const n = Number(v);
            if (!Number.isSafeInteger(n) || n <= 0)
                throw new Error(`expected a positive integer for ${field}`);
            return String(n);
        }
        case 'decimal': {
            const n = Number(v);
            if (!Number.isFinite(n) || n <= 0)
                throw new Error(`expected a positive number for ${field}`);
            return String(n);
        }
        case 'battery_chemistry': {
            // Chemistry grain (the two-axis standard) — legacy technology words a
            // machine source may still send fold to their chemistry.
            const low = v.toLowerCase();
            if (BATTERY_CHEMISTRIES.includes(low))
                return low;
            // Technology-grain words fold to their chemistry (classify-at-known-depth,
            // via the standard's own derivation); 'agm' survives as the one legacy
            // alias machines still send for the vrla_agm family.
            if (low in TECHNOLOGY_CHEMISTRY)
                return TECHNOLOGY_CHEMISTRY[low];
            if (low === 'agm')
                return 'lead_acid';
            throw new Error(`expected one of ${BATTERY_CHEMISTRIES.join(', ')} for ${field}`);
        }
        case 'text':
            return v.length > 64 ? v.slice(0, 64) : v;
    }
}
// ── the batch request (the producers' one write surface) ─────────────────────
// `field` is a plain string on the wire so an unknown field rejects that ROW
// with a report (the batch stays idempotent), never 400s the whole batch.
export const SolutionObservationItemSchema = z
    .object({
    site_id: z.string().trim().min(1).max(16),
    field: z.string().trim().min(1).max(48),
    /** Canonical-unit text; null = the source observed the thing ABSENT. */
    value: z.string().trim().max(255).nullable(),
    /** Sender-supplied sighting time (ISO). */
    discovered_at: z.string().datetime(),
})
    .strict();
export const SolutionObservationsBatchSchema = z
    .object({
    /** Producer id — one row per (site, field, source); e.g. 'discovery'. */
    source: z.string().trim().min(1).max(32),
    observations: z.array(SolutionObservationItemSchema).min(1).max(1000),
})
    .strict();
// ── the per-row report (idempotent upsert semantics — 200 with rejects) ──────
export const OBSERVATION_REJECT_CODES = ['UNKNOWN_SITE', 'UNKNOWN_FIELD', 'BAD_VALUE'];
export const ObservationRejectSchema = z
    .object({
    index: z.number().int().nonnegative(), // position in the submitted batch
    site_id: z.string(),
    field: z.string(),
    code: z.enum(OBSERVATION_REJECT_CODES),
    message: z.string(),
})
    .strict();
export const SolutionObservationsResultSchema = z
    .object({
    accepted: z.number().int().nonnegative(),
    rejected: z.array(ObservationRejectSchema),
})
    .strict();
